/**
 * class MarkdownRenderer
 *
 * Converts parsed token stream back to Markdown string. Each instance has
 * independent copy of rules. Those can be rewritten with ease.
 */
/* oxlint-disable typescript/no-non-null-assertion, unicorn/no-immediate-mutation -- renderer rule table built incrementally */

import type { Token } from './token'
import type { MarkdownExitEnv } from './types/shared'
import { unescapeAll } from './common/utils'

export type MarkdownRenderOptions = {}

export type MarkdownRenderRule = (
  tokens: Token[],
  idx: number,
  options: MarkdownRenderOptions,
  env: MarkdownExitEnv,
  self: MarkdownRenderer,
) => string

export interface MarkdownRenderRuleRecord {
  [type: string]: MarkdownRenderRule | undefined
}

const default_rules: Record<string, MarkdownRenderRule> = {}

// Inline rules
default_rules.text = (tokens, idx) => {
  return tokens[idx].content
}

// A delimiter longer than any backtick run inside, padded where the content's edges would otherwise be read as part of
// the delimiter or stripped (the backticks rule strips one space from each side of content ` x ` of 3+ characters).
default_rules.code_inline = (tokens, idx) => {
  const content = tokens[idx].content
  const fence = '`'.repeat(Math.max(0, ...[...content.matchAll(/`+/g)].map((run) => run[0].length)) + 1)
  const pad = content.startsWith('`') || content.endsWith('`') || /^ [^]+ $/.test(content)
  return pad ? `${fence} ${content} ${fence}` : `${fence}${content}${fence}`
}

default_rules.hardbreak = () => {
  return '  \n'
}

default_rules.softbreak = () => {
  return '\n'
}

default_rules.em_open = (tokens, idx) => tokens[idx].markup || '*'
default_rules.em_close = (tokens, idx) => tokens[idx].markup || '*'

default_rules.strong_open = () => '**'
default_rules.strong_close = () => '**'

default_rules.s_open = () => '~~'
default_rules.s_close = () => '~~'

default_rules.diff_inline = (tokens, idx) => {
  const token = tokens[idx]
  if (token.meta?.kind === 'update') {
    return `{~~${String(token.meta.old)}~>${String(token.meta.new)}~~}`
  }
  const marker = token.meta?.kind === 'delete' ? '--' : '++'
  return `{${marker}${token.content}${marker}}`
}

default_rules.critic_comment = (tokens, idx) => `{>>${tokens[idx].content}<<}`

// Helper to render custom attributes as {key=value ...}
/**
 * `key="value"`, or `key:="json"` for a value holding what the plain form cannot: a quote ends it, a brace ends the
 * attribute block (braces are counted without regard to quotes), a line break ends the line.
 */
export function attributeAssignment(key: string, value: string): string {
  if (!/["{}\n\r]/.test(value)) return `${key}="${value}"`
  return `${key}:=${JSON.stringify(value).replaceAll('{', '\\u007b').replaceAll('}', '\\u007d')}`
}

function renderCustomAttrs(
  attrs: [string, string][] | null,
  excludeKeys: string[],
): string {
  if (!attrs) return ''

  const customAttrs = attrs.filter(([key]) => !excludeKeys.includes(key))
  if (customAttrs.length === 0) return ''

  const attrStr = customAttrs
    .map(([key, value]) => {
      // Don't quote boolean values (true/false) or numbers
      if (value === 'true' || value === 'false' || /^\d+$/.test(value)) {
        return `${key}=${value}`
      }
      return attributeAssignment(key, value)
    })
    .join(' ')

  return `{${attrStr}}`
}

default_rules.link_open = () => '['
default_rules.link_close = (tokens, idx, _options, _env, _self) => {
  // Find the corresponding link_open token
  let openIdx = idx - 1
  let nestLevel = 1
  while (openIdx >= 0 && nestLevel > 0) {
    if (tokens[openIdx].type === 'link_close') {
      nestLevel++
    } else if (tokens[openIdx].type === 'link_open') {
      nestLevel--
    }
    if (nestLevel > 0) {
      openIdx--
    }
  }

  if (openIdx < 0 || tokens[openIdx].type !== 'link_open') {
    return ']()' // Fallback
  }

  const openToken = tokens[openIdx]
  const href = openToken.attrGet('href') || ''
  const title = openToken.attrGet('title')

  let result = ''
  if (title) {
    result = `](${href} "${title}")`
  } else {
    result = `](${href})`
  }

  // Render custom attributes
  result += renderCustomAttrs(openToken.attrs, ['href', 'title'])

  return result
}

default_rules.image = (tokens, idx, _options, _env, _self) => {
  const token = tokens[idx]
  const src = token.attrGet('src') || ''
  const alt = token.content
  const title = token.attrGet('title')

  let result = ''
  if (title) {
    result = `![${alt}](${src} "${title}")`
  } else {
    result = `![${alt}](${src})`
  }

  // Render custom attributes
  result += renderCustomAttrs(token.attrs, ['src', 'alt', 'title'])

  return result
}

// Block rules
default_rules.paragraph_open = () => ''
default_rules.paragraph_close = (tokens, idx) => {
  const token = tokens[idx]
  // Hidden paragraphs (tight lists) don't render
  if (token.hidden) {
    return ''
  }

  // Add blank line after paragraph if next token is a block element
  // BUT not if it's a closing token (blockquote_close, directive_block_close, etc)
  const nextToken = tokens[idx + 1]
  if (nextToken?.block && !nextToken.type.endsWith('_close')) {
    return '\n\n'
  }
  return '\n'
}

default_rules.heading_open = (tokens, idx) => {
  const token = tokens[idx]
  const level = Math.trunc(Number(token.tag.slice(1))) // h1 -> 1, h2 -> 2, etc
  return `${'#'.repeat(level)} `
}
default_rules.heading_close = (tokens, idx) => {
  // Add blank line after heading if next token is a block element
  const nextToken = tokens[idx + 1]
  if (nextToken?.block) {
    return '\n\n'
  }
  return '\n'
}

default_rules.code_block = (tokens, idx) => {
  const token = tokens[idx]
  const content = token.content
  return `${content
    .split('\n')
    .map((line) => `    ${line}`)
    .join('\n')}\n`
}

default_rules.fence = (tokens, idx) => {
  const token = tokens[idx]
  const info = token.info ? unescapeAll(token.info).trim() : ''
  const content = token.content
  const markup = token.markup || '```'

  return `${markup}${info}\n${content}${markup}\n`
}

default_rules.blockquote_open = () => ''
default_rules.blockquote_close = () => ''

default_rules.hr = (tokens, idx) => {
  const markup = tokens[idx].markup
  const nextToken = tokens[idx + 1]
  return `${markup}${nextToken?.block && !nextToken.type.endsWith('_close') ? '\n\n' : '\n'}`
}

default_rules.bullet_list_open = () => ''
default_rules.bullet_list_close = listCloseRule

default_rules.ordered_list_open = () => ''
default_rules.ordered_list_close = listCloseRule

// The last list item already emitted '\n'; a following sibling block needs a
// second '\n' to form a blank line, or re-parsing lazily absorbs that block
// into the last list item.
function listCloseRule(tokens: Token[], idx: number): string {
  const nextToken = tokens[idx + 1]
  if (nextToken?.block && !nextToken.type.endsWith('_close')) {
    return '\n'
  }
  return ''
}

default_rules.list_item_open = (tokens, idx) => {
  const token = tokens[idx]
  const markup = token.markup
  const info = token.info // For ordered lists, this is the number

  // Calculate indentation based on nesting level
  // Level 1 = no indent, Level 3 = 2 spaces, Level 5 = 4 spaces, etc.
  const indent =
    token.level > 1 ? '  '.repeat(Math.floor((token.level - 1) / 2)) : ''

  // Check if this is a nested list item
  // If the token before bullet_list_open/ordered_list_open is a hidden paragraph_close, we need a newline
  const prevToken = idx > 0 ? tokens[idx - 1] : null
  const needsNewline =
    prevToken &&
    (prevToken.type === 'bullet_list_open' ||
      prevToken.type === 'ordered_list_open') &&
    idx > 1 &&
    tokens[idx - 2].type === 'paragraph_close' &&
    tokens[idx - 2].hidden

  // Check if this is a task list item
  const isTask = token.attrGet('data-task') === 'true'
  const isChecked = token.attrGet('data-task-checked') === 'true'

  let prefix = needsNewline ? '\n' : ''
  if (info) {
    // Ordered list
    prefix += `${indent}${info}${markup} `
  } else {
    // Unordered list
    prefix += `${indent}${markup} `
  }

  // Add task marker if this is a task list item
  if (isTask) {
    const taskMarker = isChecked ? '[x] ' : '[ ] '
    return prefix + taskMarker
  }

  return prefix
}

default_rules.list_item_close = (tokens, idx) => {
  // Don't add newline if this list item contains a nested list at the end
  // Check if previous tokens were list_close -> list_close (nested list ending)
  const prev1 = idx > 0 ? tokens[idx - 1] : null
  const _prev2 = idx > 1 ? tokens[idx - 2] : null

  // If we just closed a nested list (bullet_list_close or ordered_list_close), don't add extra newline
  if (
    prev1 &&
    (prev1.type === 'bullet_list_close' || prev1.type === 'ordered_list_close')
  ) {
    return ''
  }

  return '\n'
}

// Table support
default_rules.table_open = () => ''
// The last row already emitted '\n'; a following sibling block needs a blank
// line, or re-parsing reads its first line as another table row.
default_rules.table_close = listCloseRule
default_rules.thead_open = () => ''
default_rules.thead_close = (tokens, idx) => {
  // Find the table_open token to get alignment info
  let tableIdx = idx - 1
  while (tableIdx >= 0 && tokens[tableIdx].type !== 'table_open') {
    tableIdx--
  }

  if (tableIdx >= 0 && tokens[tableIdx].meta?.aligns) {
    const aligns = tokens[tableIdx].meta.aligns
    const colWidths = tokens[tableIdx].meta.colWidths || []

    const separators = aligns.map((align: string, idx: number) => {
      const width = colWidths[idx] || 6
      const dashes = '-'.repeat(width)
      if (align === 'left') return `:${dashes.slice(1)}`
      if (align === 'right') return `${dashes.slice(1)}:`
      if (align === 'center') return `:${dashes.slice(2)}:`
      return dashes
    })
    return `| ${separators.join(' | ')} |\n`
  }
  // A table built without source alignment still needs its delimiter row, or it reads back as a paragraph.
  const columns = tokens.slice(tableIdx + 1, idx).filter((token) => token.type === 'th_open').length
  return columns ? `|${' --- |'.repeat(columns)}\n` : ''
}
default_rules.tbody_open = () => ''
default_rules.tbody_close = () => ''
default_rules.tr_open = () => '|'
default_rules.tr_close = () => '\n'
default_rules.th_open = () => ' '
default_rules.th_close = (tokens, idx, options, env, self) => {
  // Find table_open
  let tableIdx = idx - 1
  while (tableIdx >= 0 && tokens[tableIdx].type !== 'table_open') {
    tableIdx--
  }

  // Calculate column index
  let colIdx = 0
  for (let j = tableIdx + 1; j < idx; j++) {
    if (tokens[j].type === 'th_close') {
      colIdx++
    }
  }

  // Get content from the inline token - render it if it has children
  let content = ''
  for (let j = idx - 1; j > tableIdx; j--) {
    if (tokens[j].type === 'inline') {
      if (tokens[j].children && tokens[j].children!.length > 0) {
        content = self.renderInline(tokens[j].children!, options, env)
      } else {
        content = tokens[j].content
      }
      break
    }
  }

  // Use stored column widths from table metadata
  if (tableIdx >= 0 && tokens[tableIdx].meta?.colWidths) {
    const colWidths = tokens[tableIdx].meta.colWidths
    const width = colWidths[colIdx] || 6
    const padding = ' '.repeat(Math.max(0, width - content.length))
    return `${padding} |`
  }

  return ' |'
}
default_rules.td_open = () => ' '
default_rules.td_close = (tokens, idx, options, env, self) => {
  // Find table_open
  let tableIdx = idx - 1
  while (tableIdx >= 0 && tokens[tableIdx].type !== 'table_open') {
    tableIdx--
  }

  // Calculate column index
  let colIdx = 0
  for (let j = tableIdx + 1; j < idx; j++) {
    if (tokens[j].type === 'th_close' || tokens[j].type === 'td_close') {
      colIdx++
    }
  }

  // Get content from the inline token - render it if it has children
  let content = ''
  for (let j = idx - 1; j > tableIdx; j--) {
    if (tokens[j].type === 'inline') {
      if (tokens[j].children && tokens[j].children!.length > 0) {
        content = self.renderInline(tokens[j].children!, options, env)
      } else {
        content = tokens[j].content
      }
      break
    }
  }

  // Use stored column widths from table metadata
  if (tableIdx >= 0 && tokens[tableIdx].meta?.colWidths) {
    const colWidths = tokens[tableIdx].meta.colWidths
    const width = colWidths[colIdx] || 6
    const padding = ' '.repeat(Math.max(0, width - content.length))
    return `${padding} |`
  }

  return ' |'
}

// HTML passthrough - preserve HTML blocks and inline HTML as-is
default_rules.html_block = (tokens, idx) => {
  return tokens[idx].content
}

default_rules.html_inline = (tokens, idx) => {
  return tokens[idx].content
}

// Wiki-link support
default_rules.wikilink_open = () => '[['
default_rules.wikilink_close = () => ']]'

// Mention support
default_rules.mention_open = () => ''
default_rules.mention_close = () => ''

// Mark (flexible-markers) support
default_rules.mark_open = (tokens, idx) => {
  const token = tokens[idx]
  const classAttr = token.attrGet('class')
  if (classAttr?.startsWith('mark-')) {
    const color = classAttr.slice(5) // Remove 'mark-' prefix
    return `==${color}=`
  }
  return '=='
}
default_rules.mark_close = () => '=='

// Math support
default_rules.math_inline = (tokens, idx) => {
  const token = tokens[idx]
  const markup = token.markup || '$$'
  return `${markup}${token.content}${markup}`
}

default_rules.math_block = (tokens, idx) => {
  const token = tokens[idx]
  return `$$\n${token.content}\n$$\n`
}

// Front matter support
default_rules.front_matter = (tokens, idx) => {
  const token = tokens[idx]
  // Front matter is typically hidden, but if rendered:
  const nextToken = tokens[idx + 1]
  if (nextToken?.block) {
    return `---\n${token.content}---\n\n`
  }
  return `---\n${token.content}---\n`
}

// Directive support
// Serialize attributes preferring #id / .class shorthand
type DirectiveAttrValue = string | number | boolean | undefined
function serializeDirectiveAttributes(
  attrs: Record<string, DirectiveAttrValue>,
): string {
  const parts: string[] = []

  if (typeof attrs.id === 'string' && /^[a-zA-Z0-9_][\w-]*$/.test(attrs.id)) {
    parts.push(`#${attrs.id}`)
  } else if (attrs.id !== undefined) {
    parts.push(attributeAssignment('id', String(attrs.id)))
  }

  if (typeof attrs.class === 'string') {
    const classes = attrs.class.split(/\s+/).filter(Boolean)
    const allShorthand = classes.every((c) => /^[a-zA-Z_][\w-]*$/.test(c))
    if (allShorthand && classes.length > 0) {
      for (const c of classes) parts.push(`.${c}`)
    } else {
      parts.push(attributeAssignment('class', attrs.class))
    }
  }

  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'id' || key === 'class') continue
    if (value === true) parts.push(`${key}=true`)
    else if (value === false) parts.push(`${key}=false`)
    else if (value === undefined) parts.push(`${key}=\``)
    else if (typeof value === 'string') parts.push(attributeAssignment(key, value))
    else parts.push(`${key}=${String(value)}`)
  }

  return parts.length > 0 ? `{${parts.join(' ')}}` : ''
}

default_rules.directive_inline = (tokens, idx) => {
  const token = tokens[idx]
  const meta = token.meta || {}
  let result = `::${meta.name}`

  if (meta.content) {
    result += `[${meta.content}]`
  }

  if (meta.attributes && Object.keys(meta.attributes).length > 0) {
    result += serializeDirectiveAttributes(meta.attributes)
  }

  return result
}

default_rules.directive_block_open = (tokens, idx) => {
  const token = tokens[idx]
  const meta = token.meta || {}
  const fence = token.markup && token.markup.length >= 3 ? token.markup : ':::'
  let result = `${fence}${meta.name}`

  if (meta.content) {
    result += `[${meta.content}]`
  }

  if (meta.attributes && Object.keys(meta.attributes).length > 0) {
    result += serializeDirectiveAttributes(meta.attributes)
  }

  // For self-closing directives (nesting=0), add blank line if next token is a block
  if (token.nesting === 0) {
    const nextToken = tokens[idx + 1]
    if (nextToken?.block && !nextToken.type.endsWith('_close')) {
      return `${result}\n\n`
    }
  }

  return `${result}\n`
}

default_rules.directive_block_close = (tokens, idx) => {
  const token = tokens[idx]
  const fence = token.markup && token.markup.length >= 3 ? token.markup : ':::'
  return `${fence}\n`
}

export class MarkdownRenderer {
  /**
   * Contains render rules for tokens. Can be updated and extended.
   */
  rules: MarkdownRenderRuleRecord = {}

  constructor() {
    this.rules = { ...default_rules }
  }

  /**
   * Render a single token to markdown
   */
  renderToken(
    tokens: Token[],
    idx: number,
    options: MarkdownRenderOptions,
    env: MarkdownExitEnv,
  ): string {
    const token = tokens[idx]

    if (token.hidden) {
      return ''
    }

    // Check if we have a custom rule for this token type
    const rule = this.rules[token.type]
    if (rule) {
      return rule(tokens, idx, options, env, this)
    }

    // Default: return empty string for unknown tokens
    return ''
  }

  /**
   * Render inline tokens
   */
  renderInline(
    tokens: Token[],
    options: MarkdownRenderOptions,
    env: MarkdownExitEnv,
  ): string {
    let result = ''

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (token.type === 'inline' && token.children) {
        result += this.renderInline(token.children, options, env)
      } else {
        result += this.renderToken(tokens, i, options, env)
      }
    }

    return result
  }

  /**
   * Helper function to render a range of tokens
   */
  private renderRange(
    tokens: Token[],
    start: number,
    end: number,
    options: MarkdownRenderOptions,
    env: MarkdownExitEnv,
    prefix: string = '',
  ): string {
    let result = ''
    let i = start

    while (i < end) {
      const token = tokens[i]

      if (token.hidden) {
        i++
        continue
      }

      if (token.type === 'blockquote_open') {
        // Find matching blockquote_close
        let level = 1
        let j = i + 1
        while (j < end && level > 0) {
          if (tokens[j].type === 'blockquote_open') level++
          if (tokens[j].type === 'blockquote_close') level--
          j++
        }

        // Recursively render blockquote content with > prefix
        // Use try-catch to handle deep recursion gracefully
        try {
          const blockquoteContent = this.renderRange(
            tokens,
            i + 1,
            j - 1,
            options,
            env,
            `${prefix}> `,
          )
          // If blockquote is empty (no content), still output the marker
          if (!blockquoteContent || blockquoteContent.trim() === '') {
            result += `${prefix}>\n`
          } else {
            result += blockquoteContent
          }
        } catch {
          // If recursion fails (e.g., stack overflow), fall back to simple rendering
          result += `${prefix}> [deeply nested content]\n`
        }
        if (tokens[j]?.block && !tokens[j].type.endsWith('_close')) {
          result += `${prefix.trimEnd()}\n`
        }
        i = j
      } else {
        const rendered =
          token.type === 'inline'
            ? token.children
              ? this.renderInline(token.children, options, env)
              : ''
            : this.renderToken(tokens, i, options, env)
        if (rendered && prefix) {
          const lines = rendered.split('\n')
          const continuesLine = result !== '' && !result.endsWith('\n')
          result += lines
            .map((line, idx) => {
              // A token can continue an existing line; prefix only line starts.
              if (idx === 0 && continuesLine) {
                return line
              }
              // A trailing newline starts no content until the next token.
              if (idx === lines.length - 1 && !line) {
                return ''
              }
              return line ? prefix + line : prefix.trimEnd()
            })
            .join('\n')
        } else {
          result += rendered
        }
        i++
      }
    }

    return result
  }

  /**
   * Render tokens to markdown string
   */
  render(
    tokens: Token[],
    options: MarkdownRenderOptions,
    env: MarkdownExitEnv,
  ): string {
    return this.renderRange(tokens, 0, tokens.length, options, env, '')
  }
}
